/**
 * Web enquiries (board 2812 M3, plan step 4): a `TabStrip` of To do and Bookings.
 * To do is the channel's `TodoPiles`: Need you now holds the truthful
 * needs-attention list (new + normalized-but-unquoted enquiries) beside the saved,
 * unsent quotes (Q246), then the server's piles; Bookings is the shared list with
 * Who fixed to Web and the When row.
 * Cards render inquiry data as TEXT ONLY (SEC-4). The card title is
 * '{service label} · {name}' on the Row's who type, joined by the Row's own
 * separator (`WHO_SEP`). ⭐ board 2812 M4 (step 0 (c)): the old U+2022 bullet and the
 * 24px `.enquiry-title` went, so a web enquiry's row reads like every other row; the
 * net's two readers (inbox-populate, park-resume) moved with it.
 * Dismiss = confirm + undo (R14): status → dismissed, undo re-posts 'new'. It
 * confirms IN PLACE in the card's `ActionRow` end slot, never gold (R-2812-d).
 */
import { Fragment, type VNode } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { ActionRow, Button, Chip, EmptyState, Row, TabStrip, type TabStripView } from '@ecmb/ui';
import { whenText } from '../lib/format.js';
import {
  INBOX_POLL_MS,
  inquiryCollectionPlace,
  inquiryNeedsManualTown,
  inquiryVenue,
  type ApiInquiry,
  type InquiryStatusResult,
} from '../lib/actions.js';
import { ApiError } from '../lib/api.js';
import { serviceById, serviceLabel, type Registry } from '../store/registry.js';
import { Banner } from '../components/Banner.js';
import { TodoPiles, type NeedCard } from './todo/TodoPiles.js';
import { BookingsList, WHO_SEP } from './Bookings.js';
import { ACTION_CHIP_WORDS } from '../../shared/booking-chip-words.js';
/* ⛔ T1749 M8 (DS-21) — THE CONTACT LINE IS GONE FROM THE ENQUIRY CARD. The
 * phone, email and stated preference render on the DETAIL (the quote screen's
 * `CustomerInfoBlock`, whose `Prefers` row is graded in inbox.spec.tsx), not on
 * the scannable list. */

export type InboxProps = {
  registry: Registry;
  load: () => Promise<ApiInquiry[]>;
  onQuoteThis: (inq: ApiInquiry) => Promise<boolean>;
  /** `bookingId` is the booking the card showed (null before "Quote this"); the
   *  server refuses a card that no longer matches the row (board 2343).
   *  ⛔ board 2374: TWO SUCCESS SHAPES (`true`, `{ invoiceNote }`), so success is
   *  tested with `!== false`, never `=== true`. */
  onSetStatus: (id: number, status: 'dismissed' | 'new', bookingId: number | null) => Promise<InquiryStatusResult>;
  /** ⭐ board 2812 M3: a To do card or a Bookings row opens its booking. REQUIRED, so a
   *  mount cannot render "Open the booking" controls that do nothing. */
  onOpenBooking: (bookingId: number) => void;
  /** ⭐ board 2812 M3: after a write on a To do card (a send, Not going ahead), the
   *  Web badge moves at once (§7.5). REQUIRED for `onOpenBooking`'s reason. */
  onChanged: () => void;
};

const TABS: ReadonlyArray<{ view: TabStripView; label: string }> = [
  { view: 'todo', label: 'To do' },
  { view: 'bookings', label: 'Bookings' },
];

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; inquiries: ApiInquiry[] };

/**
 * board 2343 — has "Quote this" already made this enquiry a booking? `normalized`
 * is what normalise writes TOGETHER with `booking_id`, so either says yes; both
 * are read so a hand-built fixture with only the status still says so.
 */
function hasJob(inq: ApiInquiry): boolean {
  return inq.status === 'normalized' || inq.booking_id != null;
}

/** The id of a card's dismiss consequence sentence (board 2343 rework 1): the
 *  Yes button's `aria-describedby` and the confirm's first focus. */
function dismissHintId(inq: ApiInquiry): string {
  return `enquiry-dismiss-hint-${String(inq.inquiry_id)}`;
}

export function Inbox({ registry, load, onQuoteThis, onSetStatus, onOpenBooking, onChanged }: InboxProps) {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [confirmingId, setConfirmingId] = useState<number | null>(null);
  /** `endedJob`: the dismissed card had a booking, so dismissing ENDED that job
   *  (board 2343). A cancel cannot be undone, so that banner offers no Undo.
   *  `note` (board 2374): the ended job's invoice needs a person, said in the
   *  SAME banner, which takes focus, so the note is announced with it. */
  const [undo, setUndo] = useState<{ id: number; name: string; endedJob: boolean; note: string | null } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  /**
   * ⭐ board 2343 rework 1 (UX review) — KEYBOARD FOCUS NEVER DROPS TO <body>.
   * Opening a dismiss confirm unmounts the Dismiss button that had focus, and
   * Keep unmounts the pair; both used to leave the caret on <body>. The id of the
   * element that should take focus after the next render is parked here, and the
   * effect below moves it: into the confirm (the consequence sentence on a card
   * with a job, else the safe Keep), and back to that card's Dismiss on Keep.
   */
  const focusAfter = useRef<string | null>(null);
  /** The "dismissed" banner takes focus on appearance, as BookingDetail's
   *  cancel outcome does: the card the operator acted on is gone by then. */
  const dismissedBannerRef = useRef<HTMLDivElement | null>(null);
  /**
   * ⭐ board 2374 — AFTER UNDO, FOCUS LANDS ON THE RESTORED CARD, NEVER <body>.
   * Undo unmounts the banner and its focused button, and the card only comes back
   * with the next list. So a successful Undo parks that card's Dismiss id here,
   * and the `[state]` effect below acts on the FIRST list that lands: that
   * Dismiss if it rendered, else the screen title. ONE-SHOT: cleared when it
   * acts, and at the start of any dismiss (a failed poll sets no state, so
   * without that a parked id could pull focus off a later banner).
   * ⚠️ `after` IS THE LIST ON SCREEN WHEN UNDO WAS PRESSED, and the effect skips
   * it. An effect from a render BEFORE the Undo can run late: Preact runs a
   * render's still-pending effects at the start of the component's next render
   * (`preact/hooks/src/index.js`, `options._render`), which here is Undo's own
   * `setUndo(null)`. Without the skip, that stale run would take the request
   * against the old list (card absent) and send focus to the title.
   */
  const focusAfterList = useRef<{ id: string; after: LoadState } | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  /** In-flight guard: a double-tap must not fire two concurrent normalise
   *  POSTs (the server's read-then-insert has no row lock — M4 gate RISK). */
  const [openingId, setOpeningId] = useState<number | null>(null);

  /** Board 829 — drops a stale response that raced a newer read. Bumped by BOTH
   *  the manual reload below and the background tick, exactly as Bookings.tsx:32
   *  does, and for a sharper reason here: `dismiss` clears `confirmingId` BEFORE
   *  awaiting `onSetStatus`, so the tick's in-flight guard is OFF for the whole
   *  dismiss round-trip. Without this compare, a tick that read the list BEFORE
   *  the dismissal can land AFTER `reload()`'s post-dismissal read and put the
   *  dismissed row back on screen, underneath its own "dismissed / Undo" banner. */
  const latest = useRef(0);

  const reload = async () => {
    const seq = ++latest.current;
    try {
      const inquiries = await load();
      if (seq === latest.current) setState({ kind: 'ready', inquiries });
    } catch {
      if (seq === latest.current) setState({ kind: 'error' });
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  useEffect(() => {
    const id = focusAfter.current;
    if (id === null) return;
    focusAfter.current = null;
    document.getElementById(id)?.focus();
  }, [confirmingId]);

  useEffect(() => {
    if (undo !== null) dismissedBannerRef.current?.focus();
  }, [undo]);

  useEffect(() => {
    const parked = focusAfterList.current;
    if (parked === null || parked.after === state || state.kind === 'loading') return;
    focusAfterList.current = null;
    (document.getElementById(parked.id) ?? titleRef.current)?.focus();
  }, [state]);

  /**
   * Board 829 — THE INBOX SCREEN MUST BE ABLE TO DISCOVER A LEAD, not just
   * render the one it loaded on mount.
   *
   * T807 gave the rail badge a discovery tick, but this screen still loaded once
   * and never again. So an operator parked on the screen literally titled "New
   * inquiries" watched the badge climb to 3 while the list in front of her still
   * showed 2 — the one surface that cannot find what it exists to show.
   *
   * Modelled on Bookings.tsx:87-99, the shipped screen-level list poll, NOT on
   * App.tsx's badge tick. Four properties are load-bearing:
   *
   *  1. SUCCESS-ONLY state update. `reload()`'s catch renders the error banner,
   *     which REPLACES the whole list. That is right on mount or an explicit
   *     retry; on a timer it means one transient blip wipes a screen the
   *     operator is mid-way through reading. A failed tick leaves the last-good
   *     list standing and the next tick retries.
   *  2. STOP ON 401/429. `loadInbox` has no PollOutcome tri-state (it throws),
   *     so the status is inspected here. An unattended tick against a dead
   *     session is exactly the hammer the auth counters distrust — see the same
   *     rule at Bookings.tsx:95 and App.tsx's badge tick.
   *  3. REFS, NOT DEPS, for the live values. With `[]` deps and a direct read,
   *     the callback would capture `confirmingId === null` forever and the skip
   *     would be inert; with `[confirmingId, openingId]` deps the interval would
   *     be torn down and re-armed on every dialog toggle, resetting the 30s
   *     clock each time. Refs give a live guard AND a stable cadence.
   *  4. THE GUARD IS CHECKED PER TICK, NOT PER EFFECT — the interval keeps
   *     running while a confirm is open and merely skips those ticks. Do NOT
   *     "optimise" this into clearing the interval while busy: a dialog left
   *     open on a parked kiosk would then starve the list permanently. As
   *     written, refresh resumes on the first tick after the dialog closes.
   *
   * ⚠️ Known and accepted: while this screen is open, BOTH this tick and the
   * badge tick in App.tsx hit `/api/inquiries?status=inbox`, so 2 requests per
   * 30s. The route is auth-gated and NOT throttled, and Bookings already
   * refreshes 4x more often. ⛔ Do NOT "fix" the overlap by adding `state.screen`
   * to the App effect's dependency array — that recreates its interval on every
   * navigation, so an operator who navigates more often than the cadence would
   * never poll at all.
   */
  const busyRef = useRef(false);
  // Refreshed every render so the interval below always reads live values.
  busyRef.current = confirmingId !== null || openingId !== null;
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    const id = setInterval(() => {
      if (busyRef.current) return;
      const seq = ++latest.current;
      loadRef.current()
        .then((inquiries) => {
          if (seq === latest.current) setState({ kind: 'ready', inquiries });
        })
        .catch((e: unknown) => {
          // Deliberately NOT setState({kind:'error'}) — see property 1 above.
          if (e instanceof ApiError && (e.status === 401 || e.status === 429)) clearInterval(id);
        });
    }, INBOX_POLL_MS);
    return () => clearInterval(id);
  }, []);

  const dismiss = async (inq: ApiInquiry) => {
    setConfirmingId(null);
    focusAfterList.current = null;
    // ⭐ board 2343 — the card says which booking it showed, and the server ends
    // that job only if the row still matches. So on success, `hasJob` is the truth.
    const result = await onSetStatus(inq.inquiry_id, 'dismissed', inq.booking_id ?? null);
    if (result !== false) {
      const note = typeof result === 'object' ? result.invoiceNote : null;
      setUndo({ id: inq.inquiry_id, name: inq.name, endedJob: hasJob(inq), note });
      void reload();
    } else {
      // ⛔ NOT "nothing changed": a response lost AFTER the job was ended would
      // make that false, and the reload then drops the card. "Didn't finish" and
      // the refreshed list are true in every case, including a 409.
      setFailure("Dismissing didn't finish. The list has been refreshed: if the enquiry is still there, try again.");
      void reload();
    }
  };

  const undoDismiss = async () => {
    if (!undo || undo.endedJob) return;
    if ((await onSetStatus(undo.id, 'new', null)) !== false) {
      focusAfterList.current = { id: `enquiry-dismiss-${String(undo.id)}`, after: state };
      setUndo(null);
      void reload();
    } else {
      setFailure("Bringing the enquiry back didn't work. Try again in a moment.");
      void reload();
    }
  };


  const [view, setView] = useState<TabStripView>('todo');

  /** One enquiry card, on the shared `Row`, opened in place (R-2812-f). */
  const enquiryCard = (inq: ApiInquiry, index: number): NeedCard => {
    const quoting = inq.status === 'normalized';
    // T485: the customer's town did not reach the price book, so Helen prices
    // this one by hand. Glanceable on the card she is already reading.
    const manualTown = inquiryNeedsManualTown(inq, serviceById(registry, inq.service)?.pricingMode ?? null);
    // Full customer-stated route incl. middle stops (display-only).
    const stopAddrs = (inq.stops ?? []).map((s) => s.address).filter(Boolean);
    const route = [inq.pickup, ...stopAddrs, inq.dropoff].filter(Boolean).join(' to ');
    const asked = [
      inq.meet_greet && 'Meet & greet',
      inq.return_trip && 'Return trip',
      inq.accessible_vehicle && 'Accessible vehicle',
      inq.recurring && 'Recurring',
    ].filter(Boolean).join(', ');
    // ⛔ A POSITION, NEVER THE INQUIRY ID: the Row's id is what the click recorder
    // writes for a tap on the open body's own box.
    const key = `enquiry-${String(index)}`;
    return {
      key,
      day: inq.journey_date ?? null,
      time: inq.journey_time ?? null,
      render: (open, onToggle) => (
        <Row
          key={key}
          id={key}
          opens="in-place"
          expanded={open}
          onOpen={onToggle}
          when={whenText(inq.journey_date, inq.journey_time)}
          /* ⭐ board 2812 M4 (step 0 (c), carried from M3's close): the title is the Row's own
             who type (20px, weight 600), never the old 24px `.enquiry-title` that read louder
             than every other row, and the Row's separator (`WHO_SEP`), not a literal bullet. */
          who={`${serviceLabel(registry, inq.service)}${WHO_SEP}${inq.name}`}
          /* ⭐ board 2812 M3 fix round: `quoting…` (a quote is under way) and `Manual quote`
             (T485: the town missed the price book) were the chip; they are facts, so they
             ride the where line now, each in its own span, and the chip says the action. */
          where={(() => {
            /* ⭐ board 2812 M3 fix round 2 (UX-7): a separator only BETWEEN facts that
               are there, so a card with no route never starts "· quoting…". */
            const facts: Array<string | VNode> = [
              ...[route, inq.course, inquiryCollectionPlace(inq), inquiryVenue(inq)].filter((f): f is string => Boolean(f)),
              ...(quoting ? [<span key="quoting">quoting…</span>] : []),
              ...(manualTown ? [<span key="manual">Manual quote</span>] : []),
            ];
            return <>{facts.map((f, i) => <Fragment key={i}>{i > 0 ? ' · ' : ''}{f}</Fragment>)}</>;
          })()}
          money={inq.passengers_band ?? undefined}
          /* ⭐⭐ board 2812 M3 fix round (Captain's item 1): A NEED YOU NOW ROW SAYS THE
             ACTION. A web enquiry's next step is "Quote this", in the neutral box: an action
             is not a status, so it is never toned (conformance arm (iii)) and never green. */
          chip={<Chip neutral>{ACTION_CHIP_WORDS.quoteThis}</Chip>}
        >
          {asked && <p class="enquiry-meta">Asked for: {asked}</p>}
          {inq.notes && <p class="enquiry-notes">{inq.notes}</p>}
          {/* ⭐ board 2343 — a card with a job says what a dismiss does to it,
              BEFORE the tap that does it: the job ends, and there is no Undo. */}
          {confirmingId === inq.inquiry_id && hasJob(inq) && (
            <p class="field-hint" id={dismissHintId(inq)} tabIndex={-1}>
              This also ends the job you started, and cannot be undone. Nothing is sent to the customer.
            </p>
          )}
          {confirmingId === inq.inquiry_id ? (
            /* The primary hides during the confirm; Keep in the start group, the
               destructive Yes the one control in the END SLOT (R-2812-d). */
            <ActionRow
              end={(
                <Button
                  variant="destructive-quiet"
                  ariaDescribedBy={hasJob(inq) ? dismissHintId(inq) : undefined}
                  onClick={() => void dismiss(inq)}
                >
                  {hasJob(inq) ? 'Yes, dismiss and end the job' : 'Yes, dismiss'}
                </Button>
              )}
            >
              <Button
                variant="quiet"
                id={`enquiry-keep-${String(inq.inquiry_id)}`}
                onClick={() => {
                  focusAfter.current = `enquiry-dismiss-${String(inq.inquiry_id)}`;
                  setConfirmingId(null);
                }}
              >
                Keep
              </Button>
            </ActionRow>
          ) : (
            <ActionRow
              end={(
                <Button
                  variant="destructive-quiet"
                  id={`enquiry-dismiss-${String(inq.inquiry_id)}`}
                  onClick={() => {
                    focusAfterList.current = null;
                    focusAfter.current = hasJob(inq) ? dismissHintId(inq) : `enquiry-keep-${String(inq.inquiry_id)}`;
                    setConfirmingId(inq.inquiry_id);
                  }}
                >
                  Dismiss
                </Button>
              )}
            >
              <Button
                variant="primary"
                disabled={openingId !== null}
                onClick={() => {
                  if (openingId !== null) return;
                  setOpeningId(inq.inquiry_id);
                  void onQuoteThis(inq).then((ok) => {
                    setOpeningId(null);
                    if (!ok) setFailure("Opening this enquiry didn't work. Try again in a moment.");
                  });
                }}
              >
                {quoting ? 'Resume quote' : 'Quote this'}
              </Button>
            </ActionRow>
          )}
        </Row>
      ),
    };
  };

  return (
    // ⭐ board 2812 M3 (R-2812-g): a LIST screen, so the pane fills the width.
    <div class="screen-pane screen-pane--wide">
      <h1 class="screen-title" tabIndex={-1} ref={titleRef}>Web enquiries</h1>
      <TabStrip label="Which part of web enquiries to show" tabs={TABS} current={view} onSelect={setView} />

      {undo && !undo.endedJob && (
        <Banner kind="ok" elementRef={dismissedBannerRef} tabIndex={-1}>
          Enquiry from {undo.name} dismissed.{' '}
          <button type="button" class="btn btn--quiet btn--inline" onClick={() => void undoDismiss()}>
            Undo
          </button>
        </Banner>
      )}
      {undo && undo.endedJob && (
        <Banner kind={undo.note ? 'warn' : 'ok'} elementRef={dismissedBannerRef} tabIndex={-1}>
          Enquiry from {undo.name} dismissed. The job is under Cancelled in Bookings.
          {undo.note && <> {undo.note}</>}
        </Banner>
      )}
      {failure && <Banner kind="error">{failure}</Banner>}

      {view === 'todo' && (
        <>
          {/* ⭐ T1749 M8 round 2 (§7.2): "Nothing is known YET" is not "there is
              nothing", and the two had one treatment here. */}
          {state.kind === 'loading' && <EmptyState state="loading" headline="Getting the enquiries…" />}
          {state.kind === 'error' && (
            <Banner kind="error">
              Loading the inbox failed. The enquiries are safe on the server. Try again in a moment.{' '}
              <button type="button" class="btn btn--quiet btn--inline" onClick={() => void reload()}>
                Try again
              </button>
            </Banner>
          )}
          <TodoPiles
            channel="web"
            registry={registry}
            onOpenBooking={onOpenBooking}
            onChanged={onChanged}
            enquiries={state.kind === 'ready' ? state.inquiries.map(enquiryCard) : null}
            enquiriesFailed={state.kind === 'error'}
          />
        </>
      )}
      {view === 'bookings' && (
        <BookingsList registry={registry} onOpen={onOpenBooking} idPrefix="web-bookings" fixedWho="web" />
      )}
    </div>
  );
}
